import { FC } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { api } from "@/helper/api";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import { persistor } from "@/components/state/store";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import styles from "@/components/subjectOverview/navigateToSubjectOverviewLink.module.css";

interface Props {
  label: string;
  companyId?: string;
  subjectId?: string;
  className?: string;
  boldText?: boolean;
  shouldNavigate?: boolean;
  skipGlobalStateUpdate?: boolean;
}

const NavigateToSubjectOverviewLink: FC<Props> = ({
  companyId,
  subjectId,
  label,
  className,
  boldText = false,
  shouldNavigate = true,
  skipGlobalStateUpdate = false,
}) => {
  const dispatch = useDispatch();
  const { handleError, errorModal } = useErrorModal();

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const openSubjectOverviewInNewTab = (subjectIdToOpen?: string) => {
    const url = subjectIdToOpen
      ? `/subjectOverview/${subjectIdToOpen}`
      : `/subjectOverview`;
    window.open(url, "_blank");
  };

  const openSubjectOverviewWithGlobalState = (subjectSearchPayload: {
    value: string;
    label: string;
    companyId?: string;
  }) => {
    dispatch(setSubjectSearch(subjectSearchPayload));
    void persistor.flush().then(() => {
      openSubjectOverviewInNewTab();
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    if (companyId) {
      api.genericApiRequest({
        entity: "company",
        entityId: companyId,
        method: "get",
        successHandler: (res: any) => {
          const company = res.data;
          if (!company?.subject_id) return handleError("error_occurred");

          if (skipGlobalStateUpdate) {
            openSubjectOverviewInNewTab(company.subject_id);
            return;
          }

          openSubjectOverviewWithGlobalState({
            value: company.subject_id,
            label: company.name || label,
            companyId: company.id,
          });
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    } else if (subjectId) {
      if (skipGlobalStateUpdate) {
        openSubjectOverviewInNewTab(subjectId);
        return;
      }

      openSubjectOverviewWithGlobalState({
        value: subjectId,
        label,
      });
    } else {
      handleError("error_occurred");
    }
  };

  return (
    <>
      {subdomain === "admin" && shouldNavigate ? (
        <Link
          to={"/subjectOverview"}
          className={`
            ${className || styles.companyNameLink}
            ${boldText ? styles.boldText : ""}
          `}
          onClick={handleClick}
        >
          {label}
        </Link>
      ) : (
        <span className={className}> {label}</span>
      )}
      {errorModal}
    </>
  );
};

export default NavigateToSubjectOverviewLink;
